import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

import { api, errorMessage } from "./api";
import { OwnedLicense, UserLicenses, UserPasswordResetResult } from "./types";
import UserHistory from "./UserHistory";

export default function UserLookupPanel({ csrf, initialEmail = "", onOpenLicense }: {
  csrf: string;
  initialEmail?: string;
  onOpenLicense?: (licenseKey: string) => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [users, setUsers] = useState<UserLicenses[] | null>(null);
  const [searchedEmail, setSearchedEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [resetUser, setResetUser] = useState<UserLicenses | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetDuration, setResetDuration] = useState("2");
  const [resetDurationUnit, setResetDurationUnit] = useState(60);
  const validityMinutes = Number(resetDuration) * resetDurationUnit;
  const durationValid = Number.isInteger(Number(resetDuration)) && validityMinutes >= 15 && validityMinutes <= 10080;
  const [resetLink, setResetLink] = useState<(UserPasswordResetResult & { email: string }) | null>(null);
  const [historyUserId, setHistoryUserId] = useState("");
  const [historyVersion, setHistoryVersion] = useState(0);
  const busy = disabling || resetting;
  const [selection, setSelection] = useState<{ user: UserLicenses; license: OwnedLicense } | null>(null);

  const lookup = useCallback(async (value: string) => {
    const normalized = value.trim().toLowerCase();
    setLoading(true);
    setUsers(null);
    setSelection(null);
    setError("");
    setNotice("");
    setResetLink(null);
    setResetUser(null);
    setHistoryUserId("");
    setSearchedEmail(normalized);
    try {
      setUsers((await api.lookupUserLicenses(normalized, csrf)).users);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [csrf]);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      lookup(initialEmail);
    }
  }, [initialEmail, lookup]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    if (!loading && !busy) lookup(email);
  };

  const requestReset = async () => {
    if (!resetUser || busy || !durationValid) return;
    setResetting(true);
    setError("");
    setNotice("");
    setResetLink(null);
    try {
      const result = await api.generateUserPasswordResetLink(resetUser.id, csrf, validityMinutes);
      setResetLink({ ...result, email: resetUser.email });
      setHistoryVersion(value => value + 1);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setResetUser(null);
      setResetting(false);
    }
  };

  const disable = async () => {
    if (!selection || disabling) return;
    setDisabling(true);
    setError("");
    setNotice("");
    try {
      const updated = await api.disableUserLicense(selection.user.id, selection.license.id, csrf);
      setUsers(current => current?.map(user => user.id === selection.user.id
        ? { ...user, licenses: user.licenses.map(license => license.id === updated.id ? updated : license) }
        : user) || null);
      setNotice(`License disabled for ${selection.user.email}: ${updated.product_name}.`);
      setHistoryVersion(value => value + 1);
      setSelection(null);
    } catch (requestError) {
      setError(errorMessage(requestError));
      setSelection(null);
    } finally {
      setDisabling(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography component="h2" variant="h5">User lookup</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>
        Find an account by email to manage licenses, generate a password reset link, and review user history.
      </Typography>
      <Stack component="form" onSubmit={search} direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField id="support-user-email" label="User email" type="email" required fullWidth value={email}
          disabled={loading || busy || Boolean(selection) || Boolean(resetUser)}
          onChange={event => setEmail(event.target.value)} inputProps={{ maxLength: 254 }} />
        <Button type="submit" variant="contained" startIcon={<SearchIcon />}
          disabled={!email.trim() || loading || busy || Boolean(selection) || Boolean(resetUser)} sx={{ minWidth: 140 }}>
          Look up user
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 2 }} aria-live="assertive">{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mt: 2 }} role="status">{notice}</Alert>}
      {resetLink && <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
        <Typography component="h3" variant="h6">Password reset link generated</Typography>
        <Typography sx={{ mt: 1, mb: 2 }}>For {resetLink.email}. No email was sent. Share this link only with the account owner.</Typography>
        <TextField id="admin-reset-link" label="Password reset link" fullWidth value={resetLink.reset_url}
          InputProps={{ readOnly: true }} inputProps={{ autoComplete: "off", spellCheck: false }} />
        <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
          Expires: {new Date(resetLink.expires_at.endsWith("Z") ? resetLink.expires_at : `${resetLink.expires_at}Z`).toLocaleString()}. The link can be used once.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button onClick={async () => {
            try { await navigator.clipboard.writeText(resetLink.reset_url); setNotice("Reset link copied."); }
            catch { setError("Could not copy the link. Select and copy it from the field above."); }
          }}>Copy reset link</Button>
          <Button onClick={() => { setResetLink(null); setNotice(""); }}>Clear link</Button>
        </Stack>
      </Paper>}
      {loading && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={28} aria-label="Looking up user" /></Box>}
      {users?.length === 0 && <Alert severity="info" sx={{ mt: 2 }}>No user found for {searchedEmail}.</Alert>}
      {users?.map(user => (
        <Box key={user.id} sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography component="h3" variant="h6" sx={{ overflowWrap: "anywhere" }}>{user.email}</Typography>
            <Chip size="small" label={user.active ? "Active account" : "Inactive account"} />
          </Stack>
          {user.display_name && <Typography color="text.secondary">{user.display_name}</Typography>}
          <Typography variant="caption" color="text.secondary">User ID: {user.id}</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" disabled={busy} onClick={() => { setResetLink(null); setResetUser(user); }}>Generate reset link</Button>
            <Button disabled={busy} onClick={() => setHistoryUserId(current => current === user.id ? "" : user.id)}>{historyUserId === user.id ? "Hide history" : "View history"}</Button>
          </Stack>
          {historyUserId === user.id && <UserHistory key={user.id} userId={user.id} csrf={csrf} refreshVersion={historyVersion} />}
          <Typography component="h4" variant="h6" sx={{ mt: 3 }}>Licenses</Typography>
          {user.licenses.length === 0 ? <Typography sx={{ mt: 2 }}>This user owns no licenses.</Typography> : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small" aria-label={`Licenses for ${user.email}`} sx={{ minWidth: 650 }}>
                <TableHead><TableRow><TableCell>Product / License ID</TableCell><TableCell>Type</TableCell><TableCell>Status</TableCell><TableCell>Activation limit</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
                <TableBody>{user.licenses.map(license => (
                  <TableRow key={license.id}>
                    <TableCell>
                      {onOpenLicense ? <Button size="small" sx={{ p: 0, textAlign: "left" }}
                        disabled={busy} onClick={() => onOpenLicense(license.id)}
                        aria-label={`View license ${license.id}`}>{license.product_name}</Button>
                        : <Typography variant="body2" sx={{ fontWeight: 600 }}>{license.product_name}</Typography>}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{license.product_id}</Typography>
                      {onOpenLicense ? <Button size="small" sx={{ p: 0, fontFamily: "monospace", fontSize: "0.75rem" }}
                        disabled={busy} onClick={() => onOpenLicense(license.id)}>{license.id}</Button>
                        : <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{license.id}</Typography>}
                    </TableCell>
                    <TableCell>{license.trial_mode ? "Trial" : "Full"}</TableCell>
                    <TableCell><Chip size="small" color={license.valid ? "success" : "default"} label={license.valid ? "Enabled" : "Inactive / disabled"} /></TableCell>
                    <TableCell>{license.max_licenses}</TableCell>
                    <TableCell align="right"><Button size="small" color="error" startIcon={<BlockOutlinedIcon />}
                      disabled={!license.valid || busy} aria-label={`Disable ${license.product_name} license ${license.id}`}
                      onClick={() => setSelection({ user, license })}>Disable</Button></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ))}
      <Dialog open={Boolean(selection)} onClose={() => !disabling && setSelection(null)} aria-labelledby="disable-license-title" fullWidth maxWidth="sm">
        <DialogTitle id="disable-license-title">Disable this license?</DialogTitle>
        <DialogContent>
          <Typography>{selection?.user.email} — {selection?.license.product_name}</Typography>
          <Typography variant="caption" sx={{ display: "block", overflowWrap: "anywhere", mt: 1 }}>{selection?.license.id}</Typography>
          <Typography sx={{ mt: 2 }}>This blocks future paid activation and online validation for this license. Previously issued offline activations may continue until the software checks online.</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={disabling} onClick={() => setSelection(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={disabling} onClick={disable}>{disabling ? "Disabling…" : "Disable license"}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(resetUser)} onClose={() => !resetting && setResetUser(null)} aria-labelledby="user-reset-title" fullWidth maxWidth="sm">
        <DialogTitle id="user-reset-title">Generate password reset link?</DialogTitle>
        <DialogContent>
          <Typography>Generate a link for {resetUser?.email} to copy and share manually. No email will be sent.</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
            <TextField id="reset-link-duration" label="Valid for" type="number" value={resetDuration}
              disabled={resetting} onChange={event => setResetDuration(event.target.value)}
              inputProps={{ min: Math.ceil(15 / resetDurationUnit), max: Math.floor(10080 / resetDurationUnit), step: 1 }}
              error={!durationValid} helperText="Choose 15 minutes to 7 days." fullWidth />
            <TextField id="reset-link-duration-unit" select label="Duration unit" value={resetDurationUnit}
              disabled={resetting} onChange={event => setResetDurationUnit(Number(event.target.value))}
              SelectProps={{ native: true }} fullWidth>
              <option value={1}>Minutes</option><option value={60}>Hours</option><option value={1440}>Days</option>
            </TextField>
          </Stack>
          <Typography sx={{ mt: 2 }}>The link can be used once and expires after the selected duration. Existing reset links remain valid. The user must follow a link to choose a new password.</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={resetting} onClick={() => setResetUser(null)}>Cancel</Button>
          <Button variant="contained" disabled={resetting || !durationValid} onClick={requestReset}>{resetting ? "Generating…" : "Generate link"}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
